import type { Attempt, CoverageUnit, DiscoveryRecords } from './types.ts';

type CoverageStatus = CoverageUnit['status'];
type TargetKind = CoverageUnit['targetKind'];

const COVERAGE_STATUSES: readonly CoverageStatus[] = [
  'NOT_ATTEMPTED',
  'RUNNING',
  'PARTIAL',
  'COMPLETE',
  'FAILED',
  'SKIPPED',
];

export interface CoverageCounts {
  total: number;
  byStatus: Record<CoverageStatus, number>;
}

export function selectCoverageCounts(attempt: Attempt | null): CoverageCounts {
  const byStatus = Object.fromEntries(COVERAGE_STATUSES.map((status) => [status, 0])) as Record<
    CoverageStatus,
    number
  >;
  for (const unit of attempt?.coverage ?? []) byStatus[unit.status] += 1;
  return { total: attempt?.coverage.length ?? 0, byStatus };
}

export function isAttemptActive(attempt: Attempt | null) {
  return (
    attempt?.status === 'QUEUED' ||
    attempt?.status === 'RUNNING' ||
    attempt?.status === 'STOP_REQUESTED'
  );
}

export function attemptStatusLabel(status: Attempt['status'] | null) {
  switch (status) {
    case 'QUEUED':
      return 'Queued';
    case 'RUNNING':
      return 'Running';
    case 'PARTIAL':
      return 'Partially complete';
    case 'COMPLETE':
      return 'Completed';
    case 'FAILED':
      return 'Failed';
    case 'STOP_REQUESTED':
      return 'Stop requested';
    case 'STOPPED':
      return 'Stopped';
    default:
      return null;
  }
}

export function groupCoverageByTargetKind(
  attempt: Attempt | null,
): Map<TargetKind, CoverageUnit[]> {
  const groups = new Map<TargetKind, CoverageUnit[]>([
    ['DNS_NAME', []],
    ['IP_ADDRESS', []],
  ]);
  for (const unit of attempt?.coverage ?? []) groups.get(unit.targetKind)?.push(unit);
  return groups;
}

export function selectDiscoverySummary(records: DiscoveryRecords | null) {
  return {
    attempt: records?.attempt ?? null,
    coverage: selectCoverageCounts(records?.attempt ?? null),
    findingCount: records?.findings.length ?? 0,
    sourceRecordCount: records?.sourceRecords.length ?? 0,
    evidenceCount: records?.evidence.length ?? 0,
  };
}

const pad = (n: number) => String(n).padStart(2, '0');

/** "4:05", or "1:04:05" past an hour. */
export function clockText(seconds: number) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** "under a minute", "12 min", "1 h 5 min". */
export function minutesText(seconds: number) {
  if (seconds < 60) return 'under a minute';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const rest = minutes % 60;
  return `${Math.floor(minutes / 60)} h${rest ? ` ${rest} min` : ''}`;
}
